import { Mail, TestTube } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { useEmailSender } from '@/hooks/useEmailSender';
import { useCallback, useEffect } from 'react';

interface SenderSelectProps {
  value?: string;
  onValueChange: (value: string) => void;
  disabled?: boolean;
  showTestAccount?: boolean;
}



const TEST_ACCOUNT = {
  id: 'test-account',
  email: 'test@builditcrm.com',
  type: 'test',
  display_name: 'Test Account'
};

export function SenderSelect({ value, onValueChange, disabled, showTestAccount = false }: SenderSelectProps) {
  const { providers, loading, initialized } = useEmailSender();

  // Auto-select test account when showTestAccount is true and no value is set
  useEffect(() => {
    if (showTestAccount && !value && !loading && initialized) {
      onValueChange(TEST_ACCOUNT.id);
    }
  }, [showTestAccount, value, loading, initialized, onValueChange]);

  const handleChange = useCallback((newValue: string) => {
    if (disabled) return;
    onValueChange(newValue);
  }, [onValueChange, disabled]);

  if (loading) {
    return (
      <Select disabled>
        <SelectTrigger>
          <SelectValue placeholder="Loading..." />
        </SelectTrigger>
      </Select>
    );
  }

  // Show disabled state only if no providers AND no test account option
  if (!showTestAccount && (!initialized || providers.length === 0)) {
    return (
      <Select disabled>
        <SelectTrigger>
          <SelectValue placeholder="No email accounts connected" />
        </SelectTrigger>
      </Select>
    );
  }

  const selectedProvider = providers.find(p => p.id === value);
  const isTestAccountSelected = value === TEST_ACCOUNT.id;

  return (
    <Select
      value={value}
      onValueChange={handleChange}
      disabled={disabled}
    >
      <SelectTrigger>
        <SelectValue placeholder="Select sender">
          {selectedProvider && (
            <div className="flex items-center gap-2">
              <Mail className="h-4 w-4" />
              <span>{selectedProvider.email}</span>
              <span className="text-xs text-muted-foreground capitalize">
                ({selectedProvider.type})
              </span>
            </div>
          )}
          {isTestAccountSelected && (
            <div className="flex items-center gap-2">
              <TestTube className="h-4 w-4" />
              <span>{TEST_ACCOUNT.email}</span>
              <Badge variant="secondary" className="text-xs">
                TEST
              </Badge>
            </div>
          )}
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        {showTestAccount && (
          <SelectItem 
            key={TEST_ACCOUNT.id}
            value={TEST_ACCOUNT.id}
            className="cursor-pointer"
          >
            <div className="flex items-center gap-2">
              <TestTube className="h-4 w-4" />
              <span>{TEST_ACCOUNT.email}</span>
              <Badge variant="secondary" className="text-xs">
                TEST
              </Badge>
            </div>
          </SelectItem>
        )}
        {providers.map((provider) => (
          <SelectItem 
            key={provider.id} 
            value={provider.id}
            className="cursor-pointer"
          >
            <div className="flex items-center gap-2">
              <Mail className="h-4 w-4" />
              <span>{provider.email}</span>
              <span className="text-xs text-muted-foreground capitalize">
                ({provider.type})
              </span>
            </div>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}